import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bragus_token";

// Global axios defaults — critical for Safari iOS PWA. Without an explicit
// timeout, Safari drops the connection after ~30s of idle and surfaces the
// error as generic "Network Error" with no context, which the user cannot
// debug. Setting `timeout: 25s` gives Safari a clear signal to fail cleanly,
// then our interceptor auto-retries once on true network errors — mirrors the
// resilience Chrome has natively.
axios.defaults.timeout = 25000;
// Attach a lightweight retry interceptor for genuine network errors
// (offline, DNS blip, backend momentarily stalled). Retries ONCE with a 1.5s
// backoff, skipping auth endpoints (login/refresh) so we don't accidentally
// trigger brute-force lockout when the real cause is transient.
let _retryInstalled = false;
function _installNetRetry() {
  if (_retryInstalled) return;
  _retryInstalled = true;
  axios.interceptors.response.use(
    (r) => r,
    async (err) => {
      const cfg = err.config || {};
      const url = cfg.url || "";
      const isAuth = /\/auth\/(login|refresh|accept-invite)/.test(url);
      // Never auto-retry AWB creation — Sameday creates a fresh AWB on every
      // POST, so a silent retry can mint a duplicate courier label (double
      // billing). Response time may exceed the axios timeout; frontend
      // callers already override timeout on those endpoints.
      const isAwbCreate = /\/returns\/[^/]+\/awbs(\?|$)/.test(url) && (cfg.method || "").toLowerCase() === "post";
      // Retry only on network / timeout errors, NOT on HTTP 4xx/5xx.
      const netErr = !err.response && (err.code === "ECONNABORTED" || err.code === "ERR_NETWORK" || err.message === "Network Error");
      if (netErr && !isAuth && !isAwbCreate && !cfg.__retried) {
        cfg.__retried = true;
        await new Promise((res) => setTimeout(res, 1500));
        return axios.request(cfg);
      }
      return Promise.reject(err);
    }
  );
}
_installNetRetry();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [error, setError] = useState("");

  // attach Authorization header from localStorage on startup
  useEffect(() => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (tok) axios.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
  }, []);

  const checkSession = useCallback(async () => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) { setUser(null); return; }
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401 && user) {
          localStorage.removeItem(TOKEN_KEY);
          delete axios.defaults.headers.common["Authorization"];
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [user]);

  const login = async (email, password) => {
    setError("");
    // Login retries ONCE on Safari-style "Network Error" — the platform's
    // Emergent ingress occasionally 502s during cold-start; a single retry
    // with a longer explicit timeout gives real users the "just worked"
    // feeling and hides the transient failure that iPhone Safari surfaces
    // as an opaque "Network Error".
    const attemptLogin = async () => axios.post(`${API}/auth/login`, { email, password }, { timeout: 30000 });
    let r;
    try {
      r = await attemptLogin();
    } catch (e) {
      const netErr = !e.response && (e.code === "ECONNABORTED" || e.code === "ERR_NETWORK" || e.message === "Network Error");
      if (netErr) {
        // Give the backend 2s to recover then try one more time.
        await new Promise((res) => setTimeout(res, 2000));
        try {
          r = await attemptLogin();
        } catch (e2) {
          const finalNet = !e2.response && (e2.code === "ECONNABORTED" || e2.code === "ERR_NETWORK" || e2.message === "Network Error");
          const msg = finalNet
            ? "Serverul nu răspunde. Verifică conexiunea la internet și încearcă din nou."
            : (formatError(e2.response?.data?.detail) || e2.message);
          setError(msg);
          throw new Error(msg);
        }
      } else {
        const msg = formatError(e.response?.data?.detail) || e.message;
        setError(msg);
        throw new Error(msg);
      }
    }
    const token = r.data.access_token;
    // 2FA gate — backend returns {requires_2fa:true, pending_token, email}
    // instead of a full session when the owner must verify on this device.
    if (r.data.requires_2fa && r.data.pending_token) {
      return { requires_2fa: true, pending_token: r.data.pending_token, email: r.data.email };
    }
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    setUser(r.data.user);
    return r.data.user;
  };

  // Verify the 2FA code (TOTP or backup) after `login()` returned requires_2fa.
  const verify2FA = async (pending_token, code, label) => {
    setError("");
    try {
      const r = await axios.post(`${API}/auth/2fa/verify`, {
        token: pending_token, code, label: label || "",
      }, { timeout: 30000 });
      const at = r.data.access_token;
      if (at) {
        localStorage.setItem(TOKEN_KEY, at);
        axios.defaults.headers.common["Authorization"] = `Bearer ${at}`;
      }
      setUser(r.data.user);
      return r.data.user;
    } catch (e) {
      const msg = formatError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const acceptInvite = async (token, password, name) => {
    setError("");
    try {
      const r = await axios.post(`${API}/auth/accept-invite`, { token, password, name });
      const at = r.data.access_token;
      if (at) {
        localStorage.setItem(TOKEN_KEY, at);
        axios.defaults.headers.common["Authorization"] = `Bearer ${at}`;
      }
      setUser(r.data.user);
      return r.data.user;
    } catch (e) {
      const msg = formatError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, error, setError, login, verify2FA, logout, acceptInvite, refresh: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function formatError(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
